import React from 'react';
import * as Sentry from '@sentry/browser';
import { ErrorScreen } from '../ErrorScreen/ErrorScreen';

interface Props {
	children: React.ReactNode;
}

interface State {
	hasCriticalError: boolean;
	error?: Error;
}

const criticalErrorPatterns = [
	/Cannot read property '.*' of undefined/,
	/Cannot read properties of undefined/,
	/Cannot read properties of null/,
	/Cannot read property '.*' of null/,
	/TypeError:.*is not a function/,
	/is not defined/,
	/is not a constructor/,
	/Cannot set propert(y|ies) of (undefined|null)/,
	/null is not an object/,
	/undefined is not an object/,
	/undefined is not a function/,
	/Maximum call stack size exceeded/,
	/Minified React error/,
	/Invalid hook call/,
	/Rendered (more|fewer) hooks than during the previous render/,
	/Objects are not valid as a React child/,
	/Element type is invalid/,
	/Cannot convert undefined or null to object/,
	/Invariant Violation/,
	/ChunkLoadError/
];

export class SmartErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasCriticalError: false };
	}

	static getDerivedStateFromError(error: Error) {
		const errorMessage = error.message || error.toString() || '';
		const isCritical = criticalErrorPatterns.some((pattern) => pattern.test(errorMessage));
		return { hasCriticalError: isCritical, error };
	}

	componentDidCatch(error: Error) {
		if (this.state.hasCriticalError) {
			const url = window.location.pathname;
			const errorMessage = `🚨 Alarm: Critical error at ${url}, ${error.message}`;
			Sentry.captureMessage(errorMessage, 'fatal');
		}
	}

	handlePrevious = () => {
		this.setState({
			hasCriticalError: false,
			error: undefined
		});

		if (window.history.length > 1) {
			window.history.back();
		} else {
			// Fallback to home page or app root
			window.location.href = '/app';
		}
	};

	render(): JSX.Element {
		if (this.state.hasCriticalError) {
			return <ErrorScreen onPrevious={() => this.handlePrevious()} />;
		}

		return <>{this.props.children}</>;
	}
}
